import {take, put, takeLatest, call, delay} from "redux-saga/effects";

import {
    getSolace,
    getClient,
    initSolace,
    onSessionEvent,
    configureConnectCallback,
    getConnectionEventListener,
} from "@atg-frame-shared/push";
import log from "@atg-shared/log";
import {isWeb} from "@atg-shared/system";

import {getClientName, getRouteName} from "../helpers";
import {SOLACE_STATUS} from "../hooks";

import * as PushActions from "./pushActions";

import type {SolaceConnectedClient} from "./pushTypes";
import type {Solace, SolaceConnectionEventListener} from "@atg-frame-shared/push";
import type {Dispatch} from "redux";
import type {SagaIterator} from "redux-saga";
import type solaceNamespace from "solclientjs";

export const onConnect = (dispatch: Dispatch) => (client: Solace) => {
    dispatch(PushActions.connected(client));

    if (typeof window !== "undefined" && isWeb) {
        window.dispatchEvent(new CustomEvent(SOLACE_STATUS.CONNECTED));
    }
};

export function* initSaga(dispatch: Dispatch): SagaIterator<void> {
    const connectCallback = yield call(onConnect, dispatch);

    yield call(configureConnectCallback, connectCallback);
    let clientInfo = yield call(initSolace);

    // continuously checking for connect event to see if we are reconnected
    while (true) {
        const payload = yield take(PushActions.CONNECTED);
        if (payload) {
            const {client} = payload;
            if (
                getClientName(clientInfo) !== getClientName(client) ||
                getRouteName(clientInfo) !== getRouteName(client)
            ) {
                yield put(PushActions.reconnected(client));
                clientInfo = client;

                if (typeof window !== "undefined" && isWeb) {
                    window.dispatchEvent(new CustomEvent(SOLACE_STATUS.RECONNECTED));
                }
            }
        }
    }
}

export function* onTestSolaceEvent(
    action: PushActions.TestSolaceEventAction,
): SagaIterator<void> {
    const client: ReturnType<typeof getClient> = yield call(getClient);
    if (!client?.session) return;
    const event = {sessionEventCode: action.eventCode} as solaceNamespace.SessionEvent;
    yield call(onSessionEvent, client.session, event);
}

export function* getSolaceClient(): SagaIterator<Solace> {
    let client = yield call(getClient);

    if (!client || getClientName(client) === "" || getRouteName(client) === "") {
        const initFinishedAction = yield take(PushActions.CONNECTED);

        if (initFinishedAction.client) {
            client = initFinishedAction.client;
        }
    }

    return client;
}

export function* startSessionEventListenerSaga(dispatch: Dispatch): SagaIterator<void> {
    let connectionListener: SolaceConnectionEventListener | null = yield call(
        getConnectionEventListener,
    );

    while (!connectionListener) {
        yield delay(1000);
        connectionListener = yield call(getConnectionEventListener);
    }

    const {SessionEventCode: SolaceEventCode} = (yield call(getSolace)) as Awaited<
        ReturnType<typeof getSolace>
    >;

    let clientInfo = connectionListener.getClientInfo();

    connectionListener
        .on(SolaceEventCode.UP_NOTICE, () => dispatch(PushActions.connected(clientInfo)))
        .on(SolaceEventCode.RECONNECTED_NOTICE, () =>
            dispatch(PushActions.connected(clientInfo)),
        );

    if (connectionListener.isConnected()) {
        yield put(PushActions.connected(clientInfo));
    }

    while (true) {
        const payload = yield take(PushActions.CONNECTED);
        if (payload) {
            const {client} = payload;
            if (
                getClientName(clientInfo) !== getClientName(client) ||
                getRouteName(clientInfo) !== getRouteName(client)
            ) {
                log.debug(
                    `Solace reconnected with a different client, dispatching reconnected action`,
                    {
                        oldClient: getClientName(clientInfo),
                        newClient: getClientName(client),
                        oldRoute: getRouteName(clientInfo),
                        newRoute: getRouteName(client),
                    },
                );
                yield put(PushActions.reconnected(client));
                clientInfo = client as SolaceConnectedClient;
                connectionListener = yield call(getConnectionEventListener);
                if (connectionListener) {
                    connectionListener
                        .on(SolaceEventCode.UP_NOTICE, () =>
                            dispatch(PushActions.connected(client)),
                        )
                        .on(SolaceEventCode.RECONNECTED_NOTICE, () =>
                            dispatch(PushActions.reconnected(client)),
                        );
                }
            }
        }
    }
}

export default function* pushSaga(dispatch: Dispatch): SagaIterator<void> {
    yield takeLatest(PushActions.INIT, initSaga, dispatch);
    yield takeLatest(PushActions.TEST_SOLACE_EVENT, onTestSolaceEvent);
    yield takeLatest(
        PushActions.START_SESSION_EVENT_LISTENER,
        startSessionEventListenerSaga,
        dispatch,
    );
}
